import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import useBeamer from 'src/hooks/useBeamer'
import { ForgotPassword, ResetPassword } from 'src/routes/forgotPassword'
import Login from 'src/routes/login'
import { routes } from 'src/routes/routes'
import Protected from 'src/routes/utils/protected'

import { TOAST_CLASSNAME } from './constants'
import { GlobalErrorProvider } from './core/contexts/globalError'
import DataProvider from './state/dataState'
import UIProvider from './state/uiState'
import UserProvider from './state/userState'
import WebSocketProvider from './state/webSocketState'

import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { browserTracingIntegration, init as initSentry } from '@sentry/react'

const TOAST_TEST_AUTO_CLOSE_MS = 1000
const TOAST_DEFAULT_AUTO_CLOSE_MS = 5000

initSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment:
        import.meta.env.MODE === 'production'
            ? window.location.hostname.includes('-qa')
                ? 'qa'
                : window.location.hostname.includes('-demo')
                  ? 'demo'
                  : 'production'
            : 'development',
    integrations: [browserTracingIntegration()],
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    initialScope: {
        // Add custom `local_env` tag for filtering
        tags: { local_env: import.meta.env.VITE_SENTRY_ENVIRONMENT },
    },
})

const BeamerProvider = ({ children }: { children: React.ReactNode }) => {
    useBeamer()
    return <>{children}</>
}

const App = () => {
    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route
                path="/*"
                element={
                    <GlobalErrorProvider>
                        <BeamerProvider>
                            <Routes>
                                <Route path={routes.LOGIN} element={<Login />} />
                                <Route path={routes.FORGOT_PASSWORD} element={<ForgotPassword />} />
                                <Route path={routes.RESET_PASSWORD} element={<ResetPassword />} />
                                <Route path="/*" element={<Protected />} />
                            </Routes>
                        </BeamerProvider>
                    </GlobalErrorProvider>
                }
            />,
        ),
    )

    // Reduce the delay before a toast is automatically closed when running tests, to make sure they interfere with
    // the test instructions.
    const toastAutoClose = import.meta.env.MODE === 'test' ? TOAST_TEST_AUTO_CLOSE_MS : TOAST_DEFAULT_AUTO_CLOSE_MS
    return (
        <DataProvider>
            <UserProvider>
                <UIProvider>
                    <WebSocketProvider>
                        <MsalWrapper>
                            <RouterProvider router={router} />
                            <ToastContainer autoClose={toastAutoClose} toastClassName={TOAST_CLASSNAME} />
                        </MsalWrapper>
                    </WebSocketProvider>
                </UIProvider>
            </UserProvider>
        </DataProvider>
    )
}

const MsalWrapper = ({ children }: { children: React.ReactNode }) => {
    let clientId
    let tenantId
    if (location.hostname.startsWith('dieteren-training')) {
        // dirty trick to make dieteren-training work on production as it needs to redirect to training tenant
        clientId = import.meta.env.VITE_AZURE_SSO_CLIENT_ID_DIETEREN_TRAINING
        tenantId = import.meta.env.VITE_AZURE_SSO_TENANT_ID_TRAINING
    } else {
        clientId = import.meta.env.VITE_AZURE_SSO_CLIENT_ID_DIETEREN
        tenantId = import.meta.env.VITE_AZURE_SSO_TENANT_ID
    }

    const instance = new PublicClientApplication({
        auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: `${window.location.origin}/sso-login`,
        },
    })

    return <MsalProvider instance={instance}>{children}</MsalProvider>
}

export default App
